import React from "react";
import { useTranslation } from "react-i18next";
import { Route, RouteChildrenProps } from "react-router-dom";

import { PrintMailData, PrintType } from "../../../../commons/types/print-mail";
import * as icons from "../../../../resources/icons";
import Icon from "../../../commons/components/Icon/Icon";
import IconLink from "../../../commons/components/IconLink/IconLink";
import TabBar from "../../../commons/components/TabBar/TabBar";
import TabBarLink from "../../../commons/components/TabBar/TabBarLink";
import TabBarOverlayTransition from "../../../commons/components/TabBarOverlay/TabBarOverlayTransition";
import ProductDetailsPartial, {
  ProductDetailsPartialRenderProps
} from "../../../commons/container/ProductDetails/ProductDetailsPartial";
import SpecificationsOverlay from "../../../commons/container/ProductDetails/SpecificationsOverlay";
import getBikeCenterName from "../../../commons/libs/bike-center-name";
import { useAppHistory, useAppLocation } from "../../../commons/libs/hooks/use-app-routing";
import usePrintOrMail from "../../../commons/libs/hooks/use-print-or-mail";
import { buildProductPath } from "../../../commons/libs/path";
import { useSettings } from "../../../commons/libs/queries/use-settings";
import { ROUTES } from "../../../commons/routes";
import { useGetMobilePrintDataItem } from "../../libs/hooks/use-get-print-data-item";
import { useMobileAppDispatcher, useMobileAppSelector } from "../../store/hooks";

import AvailabilityOverlay from "./AvailabilityOverlay";

const ProductDetails = () => {
  const [dispatch, actions] = useMobileAppDispatcher();
  const { t } = useTranslation(["mobile"]);
  const history = useAppHistory();
  const location = useAppLocation();

  const settings = useSettings();
  const { printOrMail, isPrintingActive, isMailActive } = usePrintOrMail();

  const { getPrintItem } = useGetMobilePrintDataItem();

  const hasEnteredThroughDeepLink = useMobileAppSelector(state => state.mobile.hasEnteredThroughDeepLink);

  const handleLinkClickWithReplace = (
    _event: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>,
    href: string
  ) => {
    history.replace(href, location.state);
  };

  const handlePrintOrMail = ({
    event,
    href,
    data
  }: {
    event: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>;
    href: string;
    data: PrintMailData;
  }): void => {
    handleLinkClickWithReplace(event, href);
    printOrMail(data);
  };

  const renderBackButton = () => {
    const onClick = () => {
      // To prevent a mobile user from accidentally exiting the app when he enters through a deep link,
      // we redirect in place (don't push), to the main page to start a new history
      if (hasEnteredThroughDeepLink) {
        // We reset the hasEnteredThroughDeepLink-state to prevent a loop
        dispatch(actions.mobile.setHasEnteredThroughDeepLink(false));
        history.replace(ROUTES.NAVIGATION.INDEX);
      } else {
        history.goBack();
      }
    };

    const label = hasEnteredThroughDeepLink
      ? t("mobile:productDetails.backToMainMenuButton")
      : (location.state?.backLabel ?? t("mobile:productDetails.backToProductFinderButton"));

    return (
      <IconLink onClick={onClick} icon={<Icon source={icons.IconSmallArrowLeft} />} size="l">
        {label}
      </IconLink>
    );
  };

  const renderTabBar = ({ productId, product, productInitializingFirstTime }: ProductDetailsPartialRenderProps) => {
    return (
      <TabBar>
        <Route
          exact
          path={buildProductPath(ROUTES.PRODUCT_DETAILS.SPECIFICATIONS, { productId })}
          children={({ match }: RouteChildrenProps) => (
            <TabBarLink
              fadeIn={!productInitializingFirstTime}
              active={!!match}
              variant={match ? "primary" : "standout"}
              href={
                match
                  ? buildProductPath(ROUTES.PRODUCT_DETAILS.INDEX, { productId })
                  : buildProductPath(ROUTES.PRODUCT_DETAILS.SPECIFICATIONS, { productId })
              }
              label={!productInitializingFirstTime ? t("mobile:productDetails.specificationsButton") : ""}
              onClick={handleLinkClickWithReplace}
            />
          )}
        />
        {product && (isPrintingActive || isMailActive) && (
          <TabBarLink
            fadeIn={!productInitializingFirstTime}
            label={
              !productInitializingFirstTime
                ? isPrintingActive && !isMailActive
                  ? t("mobile:productDetails.printButton")
                  : t("mobile:productDetails.shareDataSheetButton")
                : ""
            }
            onClick={event =>
              handlePrintOrMail({
                event,
                href: buildProductPath(ROUTES.PRODUCT_DETAILS.INDEX, { productId }),
                data: {
                  type: PrintType.ProductDataSheet,
                  bikeCenterName: getBikeCenterName(settings.initialized.customization),
                  showPrices: settings.initialized.customization.showPrices,
                  showAssortmentPrices: settings.assortmentPriceSettings.showAssortmentPrices,
                  showFallbackPrices: settings.assortmentPriceSettings.showFallbackPrices,
                  showOriginalPrices: settings.assortmentPriceSettings.showOriginalPrices,
                  useRrpAsOriginalPrice: settings.assortmentPriceSettings.useRrpAsOriginalPrice,
                  showSaleBadge: settings.assortmentPriceSettings.showSaleBadge,
                  concealModelYear: settings.initialized.customization.modelYearDisplay.concealed,
                  modelYearDisplayTerm: settings.initialized.customization.modelYearDisplay.term,
                  currency: settings.initialized.currency,
                  items: [getPrintItem({ product })],
                  initiator: "ProductDetails"
                }
              })
            }
          />
        )}
        <Route
          exact
          path={buildProductPath(ROUTES.PRODUCT_DETAILS.AVAILABILITY, { productId })}
          children={({ match }: RouteChildrenProps) => (
            <TabBarLink
              active={!!match}
              variant={match ? "primary" : "standout"}
              fadeIn={!productInitializingFirstTime}
              href={
                match
                  ? buildProductPath(ROUTES.PRODUCT_DETAILS.INDEX, { productId })
                  : buildProductPath(ROUTES.PRODUCT_DETAILS.AVAILABILITY, { productId })
              }
              label={
                !productInitializingFirstTime
                  ? t("mobile:productDetails.variantsAvailabilityButton")
                  : t("mobile:productDetails.variantsButton")
              }
              onClick={handleLinkClickWithReplace}
            />
          )}
        />
      </TabBar>
    );
  };

  const renderTabBarOverlay = ({ productId, variantId, product, brand }: ProductDetailsPartialRenderProps) => {
    if (!product || !brand) {
      return null;
    }

    return (
      <>
        <Route
          exact
          path={buildProductPath(ROUTES.PRODUCT_DETAILS.SPECIFICATIONS, { productId })}
          children={({ match }: RouteChildrenProps) => (
            <TabBarOverlayTransition open={!!match}>
              <SpecificationsOverlay product={product} />
            </TabBarOverlayTransition>
          )}
        />
        <Route
          exact
          path={buildProductPath(ROUTES.PRODUCT_DETAILS.AVAILABILITY, { productId })}
          children={({ match }: RouteChildrenProps) => (
            <TabBarOverlayTransition open={!!match}>
              <AvailabilityOverlay product={product} brand={brand} variantId={variantId} />
            </TabBarOverlayTransition>
          )}
        />
      </>
    );
  };

  return (
    <ProductDetailsPartial backButton={renderBackButton} tabBar={renderTabBar} tabBarOverlay={renderTabBarOverlay} />
  );
};

export default ProductDetails;
